import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import type { ReactElement } from 'react';

/**
 * Sélecteur de période (24 h, 7 jours, 30 jours au tableau de bord ; 30 ou 90
 * jours aux statistiques), a etats exclusifs.
 *
 * Les periodes sont **celles que l'ecran donne** : le serveur n'en sert que de
 * predefinies, il n'y a donc jamais de saisie libre.
 *
 * Un groupe de boutons a bascule (`aria-pressed`) et non des onglets : il ne
 * change pas de panneau, il change la fenetre des chiffres. Cibles de 44 px au
 * minimum — l'ecran se consulte aussi au telephone.
 */
export const PeriodSwitch = <Key extends string>({
  value,
  onChange,
  options,
  label = 'Période des indicateurs',
}: {
  readonly value: Key;
  readonly onChange: (period: Key) => void;
  readonly options: readonly { readonly key: Key; readonly label: string }[];
  /** Nom accessible du groupe. */
  readonly label?: string;
}): ReactElement => (
  <ToggleButtonGroup
    exclusive
    aria-label={label}
    value={value}
    // MUI rend `null` quand on clique l'option deja choisie : on garde la valeur.
    onChange={(_event, next: Key | null) => {
      if (next !== null) {
        onChange(next);
      }
    }}
    sx={{ bgcolor: 'tokens.surface', border: 1, borderColor: 'tokens.border', borderRadius: '12px', p: '4px', gap: '4px' }}
  >
    {options.map((period) => (
      <ToggleButton
        key={period.key}
        value={period.key}
        sx={{
          minHeight: 44,
          minWidth: 44,
          px: 1.5,
          border: 0,
          borderRadius: '8px !important',
          textTransform: 'none',
          fontSize: 13,
          fontWeight: 600,
          color: 'tokens.textMuted',
          '&.Mui-selected': { bgcolor: 'tokens.primarySoft', color: 'tokens.primary', fontWeight: 700 },
          '&.Mui-selected:hover': { bgcolor: 'tokens.primarySoft' },
        }}
      >
        {period.label}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);
